import React, { useEffect, useState } from 'react';
import { Mail, Lock, LogIn, Loader2, ShieldCheck, Sparkles, AlertCircle, UserPlus } from 'lucide-react';
import { forgotPassword, login, register, type AuthUser } from '../services/authApi';
import { AllodosLogo } from './AllodosLogo';
import { LegalDocumentModal, type LegalDocumentKind } from './LegalDocumentModal';

interface LoginProps {
  onLogin: (user: AuthUser) => void;
  initialEmail?: string;
  initialName?: string;
  initialIsRegister?: boolean;
  /** Token do convite (`?invite=`): força o cadastro com o papel definido. */
  inviteToken?: string | null;
  /** Papel do convite, apenas para exibição. */
  inviteRoleLabel?: string | null;
  /** Abre direto no pedido de recuperação de senha (bloco 39). */
  initialForgot?: boolean;
}

const Login: React.FC<LoginProps> = ({
  onLogin,
  initialEmail = '',
  initialName = '',
  initialIsRegister = false,
  inviteToken = null,
  inviteRoleLabel = null,
  initialForgot = false,
}) => {
  const [isManual, setIsManual] = useState(true);
  const [isRegister, setIsRegister] = useState(initialIsRegister || !!inviteToken);
  // Bloco 38: documentos abertos a partir da frase de aceite, sem login.
  const [legalDoc, setLegalDoc] = useState<LegalDocumentKind | null>(null);
  // Bloco 39: "Esqueceu a senha?" — pede o e-mail e envia o link seguro.
  const [isForgot, setIsForgot] = useState(initialForgot);
  const [forgotEmail, setForgotEmail] = useState(initialEmail);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail.trim())) {
      setForgotError('Informe um e-mail válido.');
      return;
    }
    setForgotLoading(true);
    try {
      await forgotPassword(forgotEmail.trim());
      // A resposta é sempre a mesma: não revela se o e-mail tem conta.
      setForgotSent(true);
    } catch (err) {
      setForgotError(err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Não foi possível enviar agora. Tente novamente.');
    } finally {
      setForgotLoading(false);
    }
  };

  // O convite é resolvido depois da primeira renderização: quando o App
  // confirma que é um cadastro, a aba "Registrar" precisa abrir sozinha.
  useEffect(() => {
    if (initialIsRegister || inviteToken) {
      setIsRegister(true);
      setIsManual(true);
    }
  }, [initialIsRegister, inviteToken]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: initialEmail,
    password: '',
    name: initialName,
    crp: '',
    clinicName: '',
  });

  // O convite chega da API depois da montagem: quando nome/e-mail do
  // convidado aparecem, o formulário é pré-preenchido (bloco 19).
  useEffect(() => {
    if (!initialEmail && !initialName) return;
    setFormData((prev) => ({
      ...prev,
      email: initialEmail || prev.email,
      name: initialName || prev.name,
    }));
  }, [initialEmail, initialName]);

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateEmail(formData.email)) {
      setError('Por favor, insira um e-mail válido.');
      return;
    }

    const minLen = isRegister ? 8 : 6;
    if (formData.password.length < minLen) {
      setError(isRegister
        ? 'A senha deve ter no mínimo 8 caracteres.'
        : 'A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (isRegister && !formData.name.trim()) {
      setError('Informe seu nome.');
      return;
    }

    if (isRegister && !acceptTerms) {
      setError('É necessário aceitar os termos de uso e política de privacidade.');
      return;
    }

    setIsLoading(true);
    try {
      if (isRegister) {
        const res = await register({
          email: formData.email,
          password: formData.password,
          name: formData.name.trim(),
          crp: formData.crp.trim() || undefined,
          clinicName: formData.clinicName.trim() || undefined,
          inviteToken: inviteToken || undefined,
          acceptTerms: true,
        });
        onLoginUser(res.user);
      } else {
        const res = await login({ email: formData.email, password: formData.password });
        onLoginUser(res.user);
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? String((err as { message: string }).message)
        : 'Falha ao conectar com o servidor. Verifique a URL da API (VITE_API_BASE_URL) e tente novamente.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  function onLoginUser(user: AuthUser) {
    onLogin(user);
  }

  const handleGoogleLogin = () => {
    setError(null);
    setError('Login com Google ainda não disponível. Use e-mail e senha.');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden font-sans">
      {legalDoc && <LegalDocumentModal kind={legalDoc} onClose={() => setLegalDoc(null)} />}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[10%] -left-[5%] w-[50%] h-[50%] bg-indigo-600/20 blur-[120px] rounded-full animate-pulse duration-[4000ms]"></div>
        <div className="absolute -bottom-[10%] -right-[5%] w-[40%] h-[40%] bg-emerald-600/10 blur-[120px] rounded-full animate-pulse duration-[6000ms]"></div>
      </div>

      <div className="w-full max-w-md p-4 md:p-8 relative z-10 animate-in fade-in zoom-in-95 duration-700">
        <div className="text-center mb-8 flex flex-col items-center">
          <div className="w-64 mb-4 hover:scale-105 transition-transform duration-500">
            <AllodosLogo variant="gray" className="w-full h-auto" />
          </div>
          <p className="text-slate-500 font-bold uppercase tracking-[0.3em] text-[10px]">Acompanhamento Clínico Inteligente</p>
        </div>

        {inviteToken && (
          <div className="mb-6 p-5 rounded-[1.5rem] bg-indigo-500/10 border border-indigo-400/30 text-center">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-300">
              Convite para a clínica
            </p>
            <p className="text-xs text-slate-300 font-bold mt-2 leading-relaxed">
              Crie sua senha para concluir o cadastro
              {inviteRoleLabel ? <> como <strong className="text-white">{inviteRoleLabel}</strong></> : null}.
            </p>
          </div>
        )}

        <div className="bg-white/5 backdrop-blur-2xl border border-white/10 p-6 md:p-10 rounded-[2rem] md:rounded-[3rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)]">
          <div className="flex bg-slate-900/60 p-1.5 rounded-2xl mb-8">
            <button
              type="button"
              onClick={() => { setIsManual(true); setError(null); setIsRegister(false); }}
              className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isManual && !isRegister ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => { setIsManual(true); setError(null); setIsRegister(true); }}
              className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isManual && isRegister ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              Registrar
            </button>
            <button
              type="button"
              onClick={() => { setIsManual(false); setError(null); setIsRegister(false); }}
              className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${!isManual ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              Acesso Social
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-400 animate-in slide-in-from-top-2">
              <AlertCircle size={18} />
              <p className="text-xs font-bold leading-tight">{error}</p>
            </div>
          )}

          {isManual && !isForgot ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {isRegister && (
                <>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Nome completo</label>
                    <div className="relative group">
                      <UserPlus className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" size={20} />
                      <input
                        type="text"
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Dr(a). Nome"
                        className="w-full bg-slate-900/60 border border-white/5 rounded-2xl p-4.5 pl-12 text-sm text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500/50 focus:outline-none transition-all placeholder:text-slate-700"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">CRP</label>
                      <input
                        type="text"
                        value={formData.crp}
                        onChange={e => setFormData({ ...formData, crp: e.target.value })}
                        placeholder="06/12345"
                        className="w-full bg-slate-900/60 border border-white/5 rounded-2xl p-4.5 text-sm text-white focus:ring-4 focus:ring-indigo-500/10 focus:outline-none placeholder:text-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Clínica</label>
                      <input
                        type="text"
                        value={formData.clinicName}
                        onChange={e => setFormData({ ...formData, clinicName: e.target.value })}
                        placeholder="Nome da clínica"
                        className="w-full bg-slate-900/60 border border-white/5 rounded-2xl p-4.5 text-sm text-white focus:ring-4 focus:ring-indigo-500/10 focus:outline-none placeholder:text-slate-700"
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">E-mail de trabalho</label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" size={20} />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="marina@clinica.com"
                    className="w-full bg-slate-900/60 border border-white/5 rounded-2xl p-4.5 pl-12 text-sm text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500/50 focus:outline-none transition-all placeholder:text-slate-700"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Senha</label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" size={20} />
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder={isRegister ? 'Mín. 8 caracteres' : '••••••••'}
                    className="w-full bg-slate-900/60 border border-white/5 rounded-2xl p-4.5 pl-12 text-sm text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500/50 focus:outline-none transition-all placeholder:text-slate-700"
                  />
                </div>
              </div>
              {!isRegister && (
                <div className="flex justify-end -mt-2">
                  {/* Bloco 39 */}
                  <button
                    type="button"
                    onClick={() => { setIsForgot(true); setForgotEmail(formData.email); setForgotSent(false); setForgotError(null); }}
                    className="text-[10px] font-black uppercase tracking-widest text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    Esqueceu a senha?
                  </button>
                </div>
              )}
              {isRegister && (
                <label className="flex items-start gap-3 text-[10px] text-slate-400 leading-relaxed cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-0.5 rounded border-white/20"
                  />
                  {/* Bloco 38: os documentos abrem em um modal, antes de criar a conta. */}
                  <span>
                    Li e aceito os{' '}
                    <button type="button" onClick={() => setLegalDoc('terms')} className="underline text-indigo-300 hover:text-indigo-200 font-bold">Termos de Uso</button>
                    {' '}e a{' '}
                    <button type="button" onClick={() => setLegalDoc('privacy')} className="underline text-indigo-300 hover:text-indigo-200 font-bold">Política de Privacidade (LGPD)</button>.
                  </span>
                </label>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-600/50 text-white py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-2xl shadow-indigo-600/30 active:scale-[0.98] transition-all flex items-center justify-center gap-3"
              >
                {isLoading ? <Loader2 className="animate-spin" size={20} /> : <><LogIn size={20} /> {isRegister ? 'Criar conta' : 'Entrar'}</>}
              </button>
            </form>
          ) : isManual && isForgot ? (
            /* Bloco 39: pedido de recuperação de senha */
            <form onSubmit={handleForgot} className="space-y-5">
              <div className="space-y-2">
                <h3 className="text-sm font-black text-white uppercase tracking-widest">Recuperar senha</h3>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  Informe o e-mail cadastrado. Se ele tiver uma conta, enviaremos um link seguro e temporário para você criar uma nova senha.
                </p>
              </div>
              {forgotSent ? (
                <div className="p-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-bold leading-relaxed">
                  Se o e-mail informado tiver uma conta, a mensagem com o link de redefinição já foi enviada. Verifique também a caixa de spam. O link vale por 1 hora e só pode ser usado uma vez.
                </div>
              ) : (
                <>
                  {forgotError && (
                    <div className="flex gap-3 p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-bold">
                      <AlertCircle size={18} className="shrink-0" /> {forgotError}
                    </div>
                  )}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">E-mail cadastrado</label>
                    <div className="relative group">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                      <input
                        type="email"
                        required
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="marina@clinica.com"
                        className="w-full bg-slate-900/60 border border-white/5 rounded-2xl p-4.5 pl-12 text-sm text-white focus:ring-4 focus:ring-indigo-500/10 focus:outline-none placeholder:text-slate-700"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-600/50 text-white py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-2xl shadow-indigo-600/30 flex items-center justify-center gap-3"
                  >
                    {forgotLoading ? <Loader2 className="animate-spin" size={20} /> : 'Enviar link de recuperação'}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => { setIsForgot(false); setForgotSent(false); setForgotError(null); }}
                className="w-full text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-white transition-colors py-2"
              >
                Voltar para o login
              </button>
            </form>
          ) : (
            <div className="space-y-6">
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="w-full bg-white hover:bg-slate-50 text-slate-900 py-5 rounded-2xl font-black uppercase tracking-[0.1em] text-xs shadow-xl active:scale-[0.98] transition-all flex items-center justify-center gap-4 group"
              >
                {isLoading ? <Loader2 className="animate-spin text-slate-400" size={20} /> : (
                  <>
                    <div className="w-6 h-6 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-5 h-5">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    </div>
                    Continuar com Google
                  </>
                )}
              </button>
              <div className="flex gap-4 p-5 bg-indigo-500/5 rounded-3xl border border-white/5">
                <Sparkles className="text-indigo-400 shrink-0" size={20} />
                <p className="text-[10px] text-slate-400 leading-relaxed font-medium italic">
                  Utilizamos autenticação de nível bancário para garantir que os dados dos seus pacientes permaneçam 100% privados e criptografados.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center justify-center gap-3 text-slate-600">
          <ShieldCheck size={16} className="text-emerald-500" />
          <span className="text-[10px] font-black uppercase tracking-[0.2em]">Conexão Segura AES-256</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
